"use client";

import type { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
// Locale-aware `Link`: a plain `<a href="/ej-health/…">` is locale-LESS, and
// with `localePrefix: "always"` the middleware sends it to the DEFAULT locale
// (`en`) unless the reader happens to carry a `NEXT_LOCALE` cookie or a Chinese
// `Accept-Language` — and every ej-health page `notFound()`s outside zh. So a
// cold visitor, a shared link or a crawler got a 404. Measured with curl.
import { Link } from "@/i18n/routing";
import { EJHEALTH_COLORS, EJHEALTH_TYPE } from "@/lib/theme/ej-health";

interface LinkProps {
  label: string;
  /**
   * REQUIRED.
   *
   * It defaulted to `"#"` and every call site took the default, so the CTA
   * rendered, invited a click and went nowhere. Required means the compiler
   * finds a bare call site instead of a reviewer — the same fix
   * `components/ej-education/arrow-button.tsx` had.
   */
  href: string;
  variant?: "primary" | "onDark";
  icon?: ReactNode;
}

/** Translate-x arrow CTA — GSG signature interaction. */
export function ArrowLink({ label, href, variant = "primary", icon }: LinkProps) {
  const onDark = variant === "onDark";
  const color = onDark ? EJHEALTH_COLORS.textOnDark : EJHEALTH_COLORS.primary;
  return (
    // `hkej-tap hkej-tap-title hkej-tap-self` (app/styles/globals.css): the
    // colour below is an inline style, which no Tailwind `hover:text-*` can
    // beat — that is why QA saw "懸停時缺少顏色變化和箭頭移動效果". The `-self`
    // variant is required because the hovered element IS the coloured one; the
    // plain descendant selector cannot reach it.
    //
    // The arrow translate and the underline already worked and are unchanged.
    <Link
      href={href}
      className="group hkej-tap hkej-tap-title hkej-tap-self inline-flex items-center gap-2"
      style={
        {
          ...EJHEALTH_TYPE.button,
          color,
          textUnderlineOffset: 4,
          // On dark surfaces staying white is correct — only the underline
          // should change. Elsewhere darken toward the brand colour.
          ["--hkej-hover" as string]: onDark
            ? EJHEALTH_COLORS.textOnDark
            : EJHEALTH_COLORS.primaryDark,
        } as React.CSSProperties
      }
    >
      <span>{label}</span>
      <span className="inline-flex transition-transform duration-300 ease-out group-hover:translate-x-1">
        {icon ?? <ArrowRight className="h-4 w-4" />}
      </span>
    </Link>
  );
}
